import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import "../lib/leaflet";
import { MapPin, Clock, Eye, MessageCircle, Send, Trash2, CornerDownRight } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { useAuth } from "../context/AuthContext";
import { ClapIcon } from "../lib/icons";
import ShareButton from "../components/shared/ShareButton";
import XListsCTA from "../components/XListsCTA";
import api from "../lib/apiClient";

// Render comment content with @username tokens linked to /:username profiles.
const renderWithMentions = (text) => {
  if (!text) return text;
  const re = /(^|[^A-Za-z0-9_.-])@([A-Za-z0-9_.-]{2,32})/g;
  const out = [];
  let lastIdx = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    const [full, lead, handle] = m;
    const start = m.index + lead.length;
    if (start > lastIdx) out.push(text.slice(lastIdx, start));
    out.push(
      <Link
        key={`mention-${key++}`}
        to={`/${handle}`}
        className="text-emerald-400 hover:underline"
        data-testid="comment-mention-link"
      >@{handle}</Link>
    );
    lastIdx = m.index + full.length;
  }
  if (lastIdx < text.length) out.push(text.slice(lastIdx));
  return out;
};

const VideoPage = () => {
  const { id } = useParams();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  // Which top-level comment the user is currently composing a reply to.
  // null = posting a top-level comment. We only allow ONE reply box open
  // at a time so the comment list stays scannable.
  const [replyParentId, setReplyParentId] = useState(null);
  const [replyText, setReplyText] = useState("");
  const { user, token, updateCredits } = useAuth();
  const [applauding, setApplauding] = useState(false);
  // useNavigate kept for backward parity; not currently called inline
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();

  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const res = await api.get(`/videos/${id}`);
        setVideo(res.data);
        const commentsRes = await api.get(`/videos/${id}/comments`);
        setComments(commentsRes.data);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    fetchVideo();
  }, [id]);

  const handleApplaud = async () => {
    if (!user) { toast.error("Please sign in to applaud"); return; }
    setApplauding(true);
    try {
      const res = await api.post(`/videos/${id}/applaud`, {});
      toast.success("Applauded! 👏");
      updateCredits(res.data.new_balance);
      setVideo({ ...video, applaud_count: video.applaud_count + 1 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to applaud");
    }
    setApplauding(false);
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in to comment"); return; }
    if (!newComment.trim()) return;
    setSubmittingComment(true);
    try {
      const res = await api.post(`/videos/${id}/comments`,
        { content: newComment }
      );
      // New top-level comment: prepend with empty replies array so render is consistent.
      setComments([{ ...res.data.comment, replies: [] }, ...comments]);
      setVideo({ ...video, comment_count: res.data.comment_count });
      setNewComment("");
      updateCredits(res.data.new_balance);
      toast.success("Comment added!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add comment");
    }
    setSubmittingComment(false);
  };

  const handleSubmitReply = async (parentId) => {
    if (!user) { toast.error("Please sign in to reply"); return; }
    const body = (replyText || "").trim();
    if (!body) return;
    setSubmittingComment(true);
    try {
      const res = await api.post(`/videos/${id}/comments`,
        { content: body, parent_id: parentId }
      );
      // Splice the new reply onto its parent comment in local state.
      setComments(comments.map((c) =>
        c.id === parentId
          ? { ...c, replies: [...(c.replies || []), res.data.comment] }
          : c
      ));
      setVideo({ ...video, comment_count: res.data.comment_count });
      setReplyText("");
      setReplyParentId(null);
      updateCredits(res.data.new_balance);
      toast.success("Reply posted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post reply");
    }
    setSubmittingComment(false);
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const res = await api.delete(`/videos/${id}/comments/${commentId}`);
      // Remove from either the top-level list OR from any parent's replies.
      setComments(comments
        .filter((c) => c.id !== commentId)
        .map((c) => ({ ...c, replies: (c.replies || []).filter((r) => r.id !== commentId) })));
      setVideo({ ...video, comment_count: res.data.comment_count });
      toast.success("Comment deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete comment");
    }
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="animate-spin h-12 w-12 border-4 border-emerald-500 border-t-transparent rounded-full" /></div>;
  if (!video) return <div className="min-h-screen bg-black flex items-center justify-center text-white">Video not found</div>;

  return (
    <div className="min-h-screen bg-black" data-testid="video-page">
      <div className="container mx-auto px-4 py-4 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Video Player */}
          <div className="lg:col-span-2">
            <div className="relative aspect-video bg-gray-900 rounded-lg overflow-hidden">
              <video src={video.video_url} className="w-full h-full object-contain" loop autoPlay playsInline controls />
              <div className="absolute bottom-12 sm:bottom-4 left-2 sm:left-4 text-white text-xs sm:text-sm font-mono bg-black/70 px-2 sm:px-3 py-1 sm:py-2 rounded">
                <div className="flex items-center gap-1 sm:gap-2">
                  <MapPin className="h-3 w-3 sm:h-4 sm:w-4" />
                  {video.location?.city || "Unknown"}, {video.location?.country || ""}
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                  {new Date(video.created_at).toLocaleString()}
                </div>
              </div>
            </div>

            <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white" data-testid="video-page-title">{video.title}</h1>
                <Link to={`/${video.username}`} className="text-emerald-400 hover:underline">
                  @{video.username}
                </Link>
              </div>
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="text-gray-400 flex items-center gap-1 text-sm">
                  <Eye className="h-4 w-4 sm:h-5 sm:w-5" /> {video.loop_count}
                </div>
                <div className="text-gray-400 flex items-center gap-1 text-sm">
                  <MessageCircle className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-400" /> {video.comment_count || 0}
                </div>
                <Button onClick={handleApplaud} disabled={applauding || (user && video.user_id === user.id)} className="bg-pink-600 hover:bg-pink-700" data-testid="video-applaud-btn">
                  <ClapIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Applaud</span> ({video.applaud_count})
                </Button>
                <ShareButton video={video} />
              </div>
            </div>

            {/* X Lists CTA — encourage viewers to follow live updates from this
                video's region via @GlobalEyesDOTtv's curated lists. */}
            {(video.location?.city || video.location?.country) && (
              <div className="mt-4">
                <XListsCTA city={video.location?.city} country={video.location?.country} />
              </div>
            )}

            {/* Comments Section */}
            <Card className="bg-gray-900 border-gray-800 mt-6">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-white text-base sm:text-lg flex items-center gap-2">
                  <MessageCircle className="h-5 w-5 text-cyan-400" /> Comments ({video.comment_count ?? comments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                {user ? (
                  <form onSubmit={handleSubmitComment} className="flex gap-2 mb-4">
                    <Input value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment..." className="bg-gray-800 border-gray-700 text-white flex-1" maxLength={500} data-testid="comment-input" />
                    <Button type="submit" disabled={submittingComment || !newComment.trim()} className="bg-cyan-600 hover:bg-cyan-700" data-testid="submit-comment-btn">
                      <Send className="h-4 w-4" />
                    </Button>
                  </form>
                ) : (
                  <p className="text-gray-400 text-sm mb-4">Sign in to leave a comment</p>
                )}

                <ScrollArea className="h-[300px] sm:h-[400px]">
                  {comments.length > 0 ? (
                    <div className="space-y-4">
                      {comments.map((comment) => (
                        <div key={comment.id} id={`comment-${comment.id}`} className="flex gap-3 group">
                          <Link to={`/${comment.username}`}>
                            <Avatar className="h-8 w-8">
                              <AvatarImage src={comment.user_profile_image} />
                              <AvatarFallback className="bg-gray-700 text-xs">{comment.username[0].toUpperCase()}</AvatarFallback>
                            </Avatar>
                          </Link>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <Link to={`/${comment.username}`} className="text-emerald-400 hover:underline text-sm font-semibold">
                                @{comment.username}
                              </Link>
                              <span className="text-gray-500 text-xs">{new Date(comment.created_at).toLocaleDateString()}</span>
                              {user && (user.id === comment.user_id || user.id === video.user_id) && (
                                <button onClick={() => handleDeleteComment(comment.id)} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 transition-opacity" data-testid="delete-comment-btn">
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              )}
                            </div>
                            <p className="text-gray-300 text-sm mt-1 break-words">
                              {renderWithMentions(comment.content)}
                            </p>
                            {user && (
                              <button
                                onClick={() => {
                                  setReplyParentId(replyParentId === comment.id ? null : comment.id);
                                  setReplyText(replyParentId === comment.id ? "" : `@${comment.username} `);
                                }}
                                className="mt-1 text-xs text-cyan-400 hover:text-cyan-300"
                                data-testid={`reply-btn-${comment.id}`}
                              >
                                {replyParentId === comment.id ? "Cancel reply" : "Reply"}
                              </button>
                            )}

                            {/* Reply composer */}
                            {replyParentId === comment.id && (
                              <form
                                onSubmit={(e) => { e.preventDefault(); handleSubmitReply(comment.id); }}
                                className="flex gap-2 mt-2"
                                data-testid={`reply-form-${comment.id}`}
                              >
                                <Input
                                  value={replyText}
                                  onChange={(e) => setReplyText(e.target.value)}
                                  placeholder={`Reply to @${comment.username}…`}
                                  className="bg-gray-800 border-gray-700 text-white flex-1 text-sm"
                                  maxLength={500}
                                  autoFocus
                                  data-testid={`reply-input-${comment.id}`}
                                />
                                <Button
                                  type="submit"
                                  disabled={submittingComment || !replyText.trim()}
                                  className="bg-cyan-600 hover:bg-cyan-700"
                                  data-testid={`submit-reply-${comment.id}`}
                                >
                                  <Send className="h-4 w-4" />
                                </Button>
                              </form>
                            )}

                            {/* Replies (single-level only, indented) */}
                            {comment.replies && comment.replies.length > 0 && (
                              <div className="mt-3 space-y-3 pl-3 border-l-2 border-gray-800">
                                {comment.replies.map((reply) => (
                                  <div key={reply.id} id={`comment-${reply.id}`} className="flex gap-2 group/reply">
                                    <CornerDownRight className="h-3.5 w-3.5 text-gray-600 mt-1.5 shrink-0" />
                                    <Link to={`/${reply.username}`}>
                                      <Avatar className="h-6 w-6">
                                        <AvatarImage src={reply.user_profile_image} />
                                        <AvatarFallback className="bg-gray-700 text-[10px]">{reply.username[0].toUpperCase()}</AvatarFallback>
                                      </Avatar>
                                    </Link>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <Link to={`/${reply.username}`} className="text-emerald-400 hover:underline text-xs font-semibold">
                                          @{reply.username}
                                        </Link>
                                        <span className="text-gray-500 text-[10px]">{new Date(reply.created_at).toLocaleDateString()}</span>
                                        {user && (user.id === reply.user_id || user.id === video.user_id) && (
                                          <button onClick={() => handleDeleteComment(reply.id)} className="opacity-0 group-hover/reply:opacity-100 text-red-400 hover:text-red-300 transition-opacity" data-testid="delete-reply-btn">
                                            <Trash2 className="h-3 w-3" />
                                          </button>
                                        )}
                                      </div>
                                      <p className="text-gray-300 text-xs mt-0.5 break-words">
                                        {renderWithMentions(reply.content)}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No comments yet. Be the first!</p>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4 mt-4 lg:mt-0">
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-white text-base sm:text-lg">Location</CardTitle>
              </CardHeader>
              <CardContent className="p-4 sm:p-6 pt-0">
                <div className="h-36 sm:h-48 rounded-lg overflow-hidden">
                  <MapContainer center={[video.location?.lat || 0, video.location?.lng || 0]} zoom={10} style={{ height: "100%", width: "100%" }} scrollWheelZoom={false}>
                    <TileLayer
                      url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                      attribution='© OpenStreetMap contributors © CARTO'
                      subdomains="abcd"
                      maxZoom={19}
                    />
                    <Marker position={[video.location?.lat || 0, video.location?.lng || 0]} />
                  </MapContainer>
                </div>
                <p className="text-gray-400 mt-2 text-xs sm:text-sm">
                  {video.location?.city}, {video.location?.country}
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-800">
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-white text-base sm:text-lg">Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 p-4 sm:p-6 pt-0">
                <div className="flex justify-between text-gray-400 text-sm">
                  <span>Loop Count</span><span className="text-white">{video.loop_count}</span>
                </div>
                <div className="flex justify-between text-gray-400 text-sm">
                  <span>Applauds</span><span className="text-pink-400">{video.applaud_count}</span>
                </div>
                <div className="flex justify-between text-gray-400 text-sm">
                  <span>Comments</span><span className="text-cyan-400">{video.comment_count || 0}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoPage;
